import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Monitor, Moon, Search, Sun } from "lucide-react";
import { Menu } from "./Tabs";
import { Button } from "./Button";

export interface OrgOption {
  id: string;
  name: string;
  roleLabel: string;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

export function OrgSwitcher({ orgs, currentId, onSwitch, footer }: {
  orgs: OrgOption[];
  currentId: string | null;
  onSwitch: (id: string) => void;
  footer?: { label: string; onSelect: () => void };
}) {
  const current = orgs.find((o) => o.id === currentId);
  return (
    <Menu
      label="Organização"
      align="start"
      trigger={
        <button type="button" className="vx-org-btn" aria-label={`Organização atual: ${current?.name ?? "nenhuma"}. Trocar`}>
          <span className="vx-avatar" aria-hidden="true">
            {initials(current?.name ?? "?")}
          </span>
          <span className="vx-org-btn__name">{current?.name ?? "Sem organização"}</span>
          {current ? <span className="vx-org-btn__role">{current.roleLabel}</span> : null}
          <ChevronDown aria-hidden="true" />
        </button>
      }
      items={[
        ...orgs.map((o) => ({
          label: `${o.name} (${o.roleLabel})`,
          checked: o.id === currentId,
          onSelect: () => onSwitch(o.id),
        })),
        ...(footer ? [{ label: footer.label, onSelect: footer.onSelect, separatorBefore: true }] : []),
      ]}
    />
  );
}

export function SearchButton({ onOpen }: { onOpen: () => void }) {
  const mac = typeof navigator !== "undefined" && /Mac/.test(navigator.platform);
  return (
    <button type="button" className="vx-search-btn" onClick={onOpen} aria-label="Ir para (Ctrl+K)" aria-keyshortcuts="Control+K Meta+K">
      <Search aria-hidden="true" />
      <span>Ir para</span>
      <kbd className="vx-kbd">{mac ? "⌘K" : "Ctrl K"}</kbd>
    </button>
  );
}

export type ThemePref = "system" | "light" | "dark";
const THEME_KEY = "vixio.theme";

function applyTheme(t: ThemePref) {
  const el = document.documentElement;
  if (t === "system") el.removeAttribute("data-theme");
  else el.setAttribute("data-theme", t);
}

export function readTheme(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

export function useTheme(): [ThemePref, (t: ThemePref) => void] {
  const [theme, setTheme] = useState<ThemePref>(() => (typeof window === "undefined" ? "system" : readTheme()));
  useEffect(() => applyTheme(theme), [theme]);
  const set = useCallback((t: ThemePref) => {
    try {
      localStorage.setItem(THEME_KEY, t);
    } catch {
      /* navegação privada */
    }
    // Troca de tema com esmaecimento curto onde o navegador tem View Transitions (sem movimento reduzido).
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (typeof doc.startViewTransition === "function" && !reduce) doc.startViewTransition(() => applyTheme(t));
    else applyTheme(t);
    setTheme(t);
  }, []);
  return [theme, set];
}

export function ThemeMenu() {
  const [theme, setTheme] = useTheme();
  const Icon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;
  const opt = (t: ThemePref, label: string, I: typeof Sun) => ({
    label,
    icon: <I aria-hidden="true" />,
    checked: theme === t,
    onSelect: () => setTheme(t),
  });
  return (
    <Menu
      label="Tema"
      trigger={<Button variant="ghost" iconOnly label="Tema da interface" icon={<Icon />} />}
      items={[opt("system", "Igual ao sistema", Monitor), opt("light", "Claro", Sun), opt("dark", "Escuro", Moon)]}
    />
  );
}

